import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { ChevronRight, UserRound } from "lucide-react";
import {
  Body,
  Caption,
  Card,
  CardTitle,
  EmptyState,
  ErrorMessage,
  LoadingState,
  PageHeader,
  VBadge,
  VButton,
  cn,
} from "@/components/ui";
import { useRequests } from "@/v2/data/queries";
import {
  NOTIFICATION_KIND_META,
  notificationBadgeColor,
  notificationIconTone,
  relativeDays,
  useNotifications,
  type V2Notification,
} from "@/v2/meldingen/notifications";

// Meldingenpagina (PZAO-41): de volledige lijst achter de notificatiebel als
// één inbox, gesorteerd op urgentie (gangbaar patroon: gericht en team in één
// lijst). Meldingen die specifiek aan de ingelogde gebruiker gericht zijn,
// dragen een "Aan jou"-indicator. Beheerder-only (route in v2Router).
export function NotificationsPage(): React.JSX.Element {
  const { isPending, isError } = useRequests();
  const { items, total, markSeen } = useNotifications();

  // Bezoek aan de pagina markeert alles als bekeken (per gebruiker). De
  // nieuw-dots tonen de snapshot van vóór dat moment, zodat zichtbaar blijft
  // wat er sinds het vorige bezoek is bijgekomen.
  const [newAtVisit, setNewAtVisit] = useState<ReadonlySet<string> | null>(null);
  useEffect(() => {
    if (isPending || newAtVisit !== null) return;
    setNewAtVisit(new Set(items.filter((n) => n.isUnread).map((n) => n.key)));
    markSeen(items.map((n) => n.key));
  }, [isPending, items, markSeen, newAtVisit]);

  if (isPending) return <LoadingState label="Meldingen laden…" />;
  if (isError) return <ErrorMessage title="Kon de meldingen niet laden" />;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Meldingen"
        subtitle="Taakherinneringen en vervalsignalen voor jou en het team. Meldingen verdwijnen automatisch zodra de situatie opgelost is."
      />

      <Card padding="none" className="overflow-hidden">
        <div className="flex items-center gap-2 border-b border-border px-5 py-3.5">
          <CardTitle className="text-sm">Openstaand</CardTitle>
          {total > 0 ? (
            <VBadge color="neutral" size="count">
              {total}
            </VBadge>
          ) : null}
        </div>
        {total === 0 ? (
          <div className="p-5">
            <EmptyState
              title="Alles opgevolgd"
              description="Er zijn geen taakherinneringen of vervalsignalen die aandacht vragen."
              className="py-8"
            />
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {items.map((n) => (
              <NotificationRow
                key={n.key}
                notification={n}
                isNew={newAtVisit?.has(n.key) ?? false}
              />
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

function NotificationRow({
  notification,
  isNew,
}: {
  notification: V2Notification & { isPersonal: boolean };
  isNew: boolean;
}): React.JSX.Element {
  const navigate = useNavigate();
  const meta = NOTIFICATION_KIND_META[notification.kind];
  const isReminder = notification.kind === "taskReminder";

  return (
    <li>
      <VButton
        variant="ghost"
        fullWidth
        onClick={() => navigate(notification.to)}
        className="group h-auto items-center justify-start gap-4 rounded-none px-5 py-3.5 text-left font-normal hover:bg-muted/50 focus-visible:ring-inset focus-visible:ring-offset-0"
      >
        <span className="relative shrink-0">
          {/* Visueel onderscheid (PZAO-41): taakherinnering = vierkante tegel,
              vervalsignaal = ronde tegel. */}
          <span
            className={cn(
              "flex size-9 items-center justify-center",
              isReminder ? "rounded-lg" : "rounded-full",
              notification.taskDone
                ? "bg-success-50 text-success-600"
                : notificationIconTone(notification.days),
            )}
          >
            <meta.icon className="size-4" aria-hidden />
          </span>
          {isNew ? (
            <span
              className="absolute -right-0.5 -top-0.5 size-2.5 rounded-full bg-info-500 ring-2 ring-card"
              aria-label="Nieuw"
            />
          ) : null}
        </span>
        <span className="min-w-0 flex-1">
          <Body weight={isNew ? "semibold" : "medium"} className="truncate">
            {notification.title}
          </Body>
          <Caption tone="muted" as="p" className="truncate">
            {notification.context}
          </Caption>
          <Caption tone="muted" as="p" className="truncate">
            {meta.label} · {notification.timing}
          </Caption>
        </span>
        {isReminder ? (
          <VBadge color={notification.taskDone ? "success" : "neutral"}>
            {notification.taskDone ? "Afgewerkt" : "Open"}
          </VBadge>
        ) : null}
        {notification.isPersonal ? (
          <VBadge color="primary" className="gap-1">
            <UserRound className="size-3" aria-hidden />
            Aan jou
          </VBadge>
        ) : null}
        <VBadge
          color={notification.taskDone ? "success" : notificationBadgeColor(notification.days)}
        >
          {relativeDays(notification.days)}
        </VBadge>
        <ChevronRight
          className="size-4 shrink-0 text-neutral-300 transition-colors group-hover:text-neutral-400"
          aria-hidden
        />
      </VButton>
    </li>
  );
}
